import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useProCalculator } from '../hooks/useProCalculator';
import ProjectManager from './ProjectManager';
import supabase from '../lib/supabase';
import { normalizeDetailAmounts } from '../utils/calculatorUtils';
import '../styles/ProCalculator.css';
import '../styles/IcingPlansSection.css';

const IcingPlansSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    reference,
    useDefaultReference,
    cookieRows,
    setRows,
    dimensionsRows,
    colorRows,
    colorNames,
    orderRows,
    useSets,
    isMetric,
    isMetricWeight,
    numberOfOrders,
    detailAmounts,
    setReference,
    setUseDefaultReference,
    setCookieRows,
    setSetRows,
    setDimensionsRows,
    setColorRows,
    setColorNames,
    setOrderRows,
    setUseSets,
    setActiveSection,
    setDetailAmounts,
    loadedPlanId,
    setLoadedPlanId,
    setLoadedPlanName,
    markSaved,
    user,
  } = useProCalculator();

  // Auto-load plan when arriving via ?resumePlan=<id> from the Home page card
  useEffect(() => {
    const planId = searchParams.get('resumePlan');
    if (!planId || !user) return;

    // Remove the param immediately so a refresh doesn't re-trigger
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('resumePlan');
      return next;
    }, { replace: true });

    supabase
      .from('projects')
      .select('id, name, data')
      .eq('id', planId)
      .eq('user_id', user.id)
      .single()
      .then(({ data: row, error }) => {
        if (error || !row) return;
        const planData = typeof row.data === 'string' ? JSON.parse(row.data) : row.data;
        if (!planData || typeof planData !== 'object') return;

        setLoadedPlanId(row.id);
        setLoadedPlanName(row.name);
        markSaved();

        if (planData.reference) setReference(planData.reference);
        if (planData.useDefaultReference !== undefined) setUseDefaultReference(planData.useDefaultReference);
        if (planData.cookieRows) setCookieRows(planData.cookieRows);
        if (planData.setRows) setSetRows(planData.setRows);
        if (planData.dimensionsRows) setDimensionsRows(planData.dimensionsRows);
        if (planData.colorRows) setColorRows(planData.colorRows);
        if (planData.colorNames) setColorNames(planData.colorNames);
        if (planData.orderRows) setOrderRows(planData.orderRows);
        if (planData.useSets !== undefined) setUseSets(planData.useSets);
        setDetailAmounts(normalizeDetailAmounts(planData.detailAmounts, (planData.colorNames || ['White']).length));

        setTimeout(() => setActiveSection('results'), 100);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Function to clear all calculator data
  const clearAllData = () => {
    // Clear loaded plan tracking
    setLoadedPlanId(null);
    setLoadedPlanName(null);
    markSaved();
    
    // Reset reference cookie
    setReference({
      width: '',
      length: '',
      weightNoIcing: '',
      weight: ''
    });
    setUseDefaultReference(true);

    // Build a fresh cookie ID so all linked rows stay in sync
    const freshCookieId = `cookie_${Date.now()}`;

    // Reset cookie rows
    setCookieRows([{
      id: freshCookieId,
      cookieName: '',
      type: 'rectangle',
      width: '',
      length: '',
      diameter: '',
      unit: 'in',
      notes: '',
      isMetric: false
    }]);

    // Reset set rows
    setSetRows([{
      id: `set_${Date.now()}`,
      setName: 'Set 1',
      cookieId: '',
      count: 1
    }]);

    // Reset dimensions rows
    setDimensionsRows([{
      id: `dim_${Date.now()}`,
      cookieId: freshCookieId,
      shapeType: 'rectangle',
      width: '',
      length: '',
      diameter: '',
      area: 0,
      icingNeeded: 0
    }]);

    // Reset color rows
    setColorRows([{
      id: `col_${Date.now()}`,
      cookieId: freshCookieId,
      percentages: [100]
    }]);

    // Reset color names
    setColorNames(['White']);

    // Reset order rows
    setOrderRows([{
      id: `ord_${Date.now()}`,
      cookieId: freshCookieId,
      count: 0
    }]);
    setDetailAmounts({ detail: [0], floral: [0], other: [0] });

    // Reset useSets flag
    setUseSets(false);
    
    // Navigate to first section after a brief delay to allow state to propagate
    setTimeout(() => {
      setActiveSection('reference');
    }, 100);
  };

  return (
    <div className="icing-plans-section">
      <div className="section-header">
        <h2 className="section-title">Saved Plans</h2>
      </div>
      <p className="saved-plans-intro">
        Every plan you've saved lives here. Open one to keep working on it, duplicate it for a similar order, or clear out ones you don't need anymore.
      </p>
      <ProjectManager
        calculatorState={{
          reference,
          useDefaultReference,
          cookieRows,
          setRows,
          dimensionsRows,
          colorRows,
          colorNames,
          orderRows,
          useSets,
          isMetric,
          isMetricWeight,
          numberOfOrders,
          detailAmounts
        }}
        onClearAllData={clearAllData}
        onLoadProject={(loadedState, planId, planName, destination = 'reference') => {
          // Set loaded plan tracking
          if (planId) setLoadedPlanId(planId);
          if (planName) setLoadedPlanName(planName);
          markSaved();
          
          // Load the project data into the calculator
          if (loadedState.reference) setReference(loadedState.reference);
          if (loadedState.useDefaultReference !== undefined) setUseDefaultReference(loadedState.useDefaultReference);
          if (loadedState.cookieRows) setCookieRows(loadedState.cookieRows);
          if (loadedState.setRows) setSetRows(loadedState.setRows);
          if (loadedState.dimensionsRows) setDimensionsRows(loadedState.dimensionsRows);
          if (loadedState.colorRows) setColorRows(loadedState.colorRows);
          if (loadedState.colorNames) setColorNames(loadedState.colorNames);
          if (loadedState.orderRows) setOrderRows(loadedState.orderRows);
          if (loadedState.useSets !== undefined) setUseSets(loadedState.useSets);
          setDetailAmounts(normalizeDetailAmounts(loadedState.detailAmounts, (loadedState.colorNames || ['White']).length));
          // Note: isMetric, isMetricWeight, and numberOfOrders are not settable via context
          // They are fixed values (inches/grams) in Pro Calculator
          
          // Navigate to the requested section after loading
          setTimeout(() => {
            setActiveSection(destination);
          }, 100);
        }}
        onProjectAction={(action, message) => {
          // Handle project actions (notifications, etc.)
        }}
        isSubscribed={true}
        onProjectUpdated={(planId, planName) => {
          if (planId && planName && planId === loadedPlanId) {
            setLoadedPlanName(planName);
          }
        }}
      />
    </div>
  );
};

export default IcingPlansSection;
